import React, { useState, ChangeEvent } from "react";
import { headerStyle, labelStyle, sectionStyle } from "./formConstants";
import InstaColors, { useSharedValue } from "../components/Constants/constants";
import "../Forms/style.css";
import {
  FormCheckBox,
  FormDate,
  FormInput,
  FormsubTitle,
  FormTitle,
  createRequiredLabel,
} from "./common";
import CountryDropdown from "./CountryDropdown";
import { W8BENEFormModel } from "../Models/W8BENEModels";
import { useW8BENEForm } from "../Hooks/useW8BENEForm";
import { W8BENEService } from "../services/W8BENEService";
import { validateTINFormat } from "../utils/formUtils";

// Add utility function for address parsing
const parseAddressComponents = (addressString: string) => {
  if (!addressString) return { city: "", state: "", postalCode: "" };

  // Split by comma and trim whitespace
  const parts = addressString.split(",").map((part) => part.trim());

  // Initialize result object
  const result = {
    city: "",
    state: "",
    postalCode: "",
  };

  // If we have at least one part, it's the city
  if (parts.length > 0) {
    result.city = parts[0];
  }

  // If we have a second part, it's the state
  if (parts.length > 1) {
    result.state = parts[1];
  }

  // If we have a third part, it's the postal code
  if (parts.length > 2) {
    result.postalCode = parts[2];
  }

  return result;
};

// TIN formatting functions
const formatEIN = (value: string) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // Format as XX-XXXXXXX
  if (digits.length >= 3) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
  } else if (digits.length >= 1) {
    return digits.slice(0, 2);
  }
  return digits;
};

const formatSSN = (value: string) => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // Format as XXX-XX-XXXX
  if (digits.length >= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  } else if (digits.length >= 4) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
  } else if (digits.length >= 1) {
    return digits.slice(0, 3);
  }
  return digits;
};

interface W8BENEFormProps {
  selectedRowData?: any;
  row?: any;
  selectedIndex?: number;
  onSaveSuccess?: (row: any) => void;
}

const W8BENEForm = ({
  selectedRowData,
  row,
  selectedIndex = 0,
  onSaveSuccess,
}: W8BENEFormProps) => {
  const { isMenuOpen } = useSharedValue();

  // Use W8BEN-E form hook for state management
  const {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  } = useW8BENEForm({
    initialData: selectedRowData,
  });

  // Form data is now just a reference to the model (single source of truth)
  const formData = model as any;

  // Watch for changes in model data and update treaty claims accordingly
  React.useEffect(() => {
    const extractedClaims: any[] = [];
    const data = model as any;

    // Look for treaty fields with indices (BENE-15-Treaty_article_0, BENE-15-Treaty_article_1, etc.)
    let claimIndex = 0;
    while (true) {
      const hasClaimData =
        data[`W8-BENE-15-Treaty_article_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_percentage_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_typeOfIncome_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_paragraph_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_subparagraph_${claimIndex}`];

      if (!hasClaimData && claimIndex === 0) {
        // Check for legacy fields without index (for the first claim)
        const hasLegacyData =
          data["W8-BENE-15_treaty_provision_and_paragraph"] ||
          data["W8-BENE-15_withholding_rate_percentage"] ||
          data["W8-BENE-15_income_type"];

        if (hasLegacyData) {
          extractedClaims.push({
            article: data["W8-BENE-15_treaty_provision_and_paragraph"] || "",
            paragraph: "",
            subparagraph: "",
            percentage: data["W8-BENE-15_withholding_rate_percentage"] || "",
            typeOfIncome: data["W8-BENE-15_income_type"] || "",
          });
          claimIndex++;
          continue;
        }
      }

      if (!hasClaimData) break;

      extractedClaims.push({
        article: data[`W8-BENE-15-Treaty_article_${claimIndex}`] || "",
        paragraph: data[`W8-BENE-15-Treaty_paragraph_${claimIndex}`] || "",
        subparagraph: data[`W8-BENE-15-Treaty_subparagraph_${claimIndex}`] || "",
        percentage: data[`W8-BENE-15-Treaty_percentage_${claimIndex}`] || "",
        typeOfIncome: data[`W8-BENE-15-Treaty_typeOfIncome_${claimIndex}`] || "",
      });

      claimIndex++;
    }

    if (extractedClaims.length > 0) {
      setTreatyClaims(extractedClaims);
    }
  }, [model]);

  // State to control validation summary display
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // State for TIN validation message
  const [tinValidationMessage, setTinValidationMessage] = useState<string | null>(null);

  // State for GIIN validation message
  const [giinValidationMessage, setGiinValidationMessage] = useState<string | null>(null);

  const [treatyClaims, setTreatyClaims] = useState<any[]>(() => {
    // Initialize treatyClaims from selectedRowData if it exists
    if (selectedRowData && selectedRowData.treatyClaims) {
      try {
        return typeof selectedRowData.treatyClaims === "string"
          ? JSON.parse(selectedRowData.treatyClaims)
          : selectedRowData.treatyClaims;
      } catch (error) {
        console.error("Error parsing treaty claims:", error);
      }
    }

    // Also check for individual treaty fields from model/selectedRowData
    const extractedClaims: any[] = [];
    const data = selectedRowData || {};

    // Look for treaty fields with indices (W8-BENE-15-Treaty_article_0, W8-BENE-15-Treaty_article_1, etc.)
    let claimIndex = 0;
    while (true) {
      const hasClaimData =
        data[`W8-BENE-15-Treaty_article_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_percentage_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_typeOfIncome_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_paragraph_${claimIndex}`] ||
        data[`W8-BENE-15-Treaty_subparagraph_${claimIndex}`];

      if (!hasClaimData && claimIndex === 0) {
        // Check for legacy fields without index (for the first claim)
        const hasLegacyData =
          data["W8-BENE-15_treaty_provision_and_paragraph"] ||
          data["W8-BENE-15_withholding_rate_percentage"] ||
          data["W8-BENE-15_income_type"];

        if (hasLegacyData) {
          extractedClaims.push({
            article: data["W8-BENE-15_treaty_provision_and_paragraph"] || "",
            paragraph: "",
            subparagraph: "",
            percentage: data["W8-BENE-15_withholding_rate_percentage"] || "",
            typeOfIncome: data["W8-BENE-15_income_type"] || "",
          });
          claimIndex++;
          continue;
        }
      }

      if (!hasClaimData) break;

      extractedClaims.push({
        article: data[`W8-BENE-15-Treaty_article_${claimIndex}`] || "",
        paragraph: data[`W8-BENE-15-Treaty_paragraph_${claimIndex}`] || "",
        subparagraph: data[`W8-BENE-15-Treaty_subparagraph_${claimIndex}`] || "",
        percentage: data[`W8-BENE-15-Treaty_percentage_${claimIndex}`] || "",
        typeOfIncome: data[`W8-BENE-15-Treaty_typeOfIncome_${claimIndex}`] || "",
      });

      claimIndex++;
    }

    if (extractedClaims.length > 0) {
      return extractedClaims;
    }

    // Default state if no treaty data found
    return [
      {
        article: "",
        paragraph: "",
        subparagraph: "",
        percentage: "",
        typeOfIncome: "",
      },
    ];
  });

  // Set validation messages from model on load - CRITICAL UX FIX
  React.useEffect(() => {
    if (model && typeof model === "object") {
      // Set TIN validation message from model if it exists
      if ((model as any).tinValidationMessage !== undefined) {
        setTinValidationMessage((model as any).tinValidationMessage);
      }

      // Set GIIN validation message from model if it exists
      if ((model as any).giinValidationMessage !== undefined) {
        setGiinValidationMessage((model as any).giinValidationMessage);
      }
    }
  }, []);

  const handleTreatyClaimChange = (
    idx: number,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Map field names to treaty claim properties
    let fieldName = "";
    if (name.includes("article")) {
      fieldName = "article";
    } else if (name.includes("paragraph") && !name.includes("subparagraph")) {
      fieldName = "paragraph";
    } else if (name.includes("subparagraph") || name.includes("subparagraph info")) {
      fieldName = "subparagraph";
    } else if (name.includes("percentage")) {
      fieldName = "percentage";
    } else if (name.includes("type of income")) {
      fieldName = "typeOfIncome";
    }

    setTreatyClaims((prev) =>
      prev.map((claim, i) => (i === idx ? { ...claim, [fieldName]: value } : claim))
    );

    // Also update the model with the W8-BENE-15-* prefixed fields (matching BEN form pattern)
    const updates: any = {};
    updates[`W8-BENE-15-Treaty_article_${idx}`] = name.includes("article")
      ? value
      : treatyClaims[idx]?.article || "";
    updates[`W8-BENE-15-Treaty_paragraph_${idx}`] =
      name.includes("paragraph") && !name.includes("subparagraph")
        ? value
        : treatyClaims[idx]?.paragraph || "";
    updates[`W8-BENE-15-Treaty_subparagraph_${idx}`] =
      name.includes("subparagraph") || name.includes("subparagraph info")
        ? value
        : treatyClaims[idx]?.subparagraph || "";
    updates[`W8-BENE-15-Treaty_percentage_${idx}`] = name.includes("percentage")
      ? value
      : treatyClaims[idx]?.percentage || "";
    updates[`W8-BENE-15-Treaty_typeOfIncome_${idx}`] = name.includes("type of income")
      ? value
      : treatyClaims[idx]?.typeOfIncome || "";

    updateMultipleFields(updates);
  };

  const handleAddTreatyClaim = () => {
    setTreatyClaims((prev) => [
      ...prev,
      { article: "", paragraph: "", subparagraph: "", percentage: "", typeOfIncome: "" },
    ]);
  };

  const handleRemoveTreatyClaim = (idx: number) => {
    setTreatyClaims((prev) => prev.filter((_, i) => i !== idx));
  };

  /**
   * Professional Checkbox Handler - Production Quality Implementation
   *
   * Handles all checkbox interactions with atomic updates to prevent state conflicts.
   * Implements enterprise-level error handling and logging.
   *
   * @param fieldName - The name of the checkbox field
   * @param isChecked - The new checked state
   */
  const handleCheckboxChange = React.useCallback(
    (fieldName: string, isChecked: boolean): void => {
      try {
        // Input validation
        if (!fieldName || typeof fieldName !== "string") {
          console.error("🚨 Invalid checkbox field name:", fieldName);
          return;
        }

        if (typeof isChecked !== "boolean") {
          console.error("🚨 Invalid checkbox value:", isChecked);
          return;
        }

        // Update the model directly
        updateField(fieldName, isChecked);
      } catch (error) {
        console.error("🚨 Professional Checkbox Handler Error:", {
          field: fieldName,
          value: isChecked,
          error: error instanceof Error ? error.message : "Unknown error",
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    },
    [model, updateField]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;

    // Handle address field splitting - update multiple fields via model
    if (name === "Permanent city address") {
      const address = parseAddressComponents(value);
      updateMultipleFields({
        [name]: value,
        "Permanent address city": address.city,
        "Permanent address state NonUS": address.state,
        "Permanent address zip": address.postalCode,
      });
    } else if (name === "Mailing address city") {
      const address = parseAddressComponents(value);
      updateMultipleFields({
        [name]: value,
        "Mailing address city parsed": address.city,
        "Mailing address state NonUS": address.state,
        "Mailing address zip": address.postalCode,
      });
    } else if (name === "Permanent address city") {
      // Handle city field changes and update combined field
      const newCombined =
        `${value}, ${formData["Permanent address state NonUS"] || ""}, ${formData["Permanent address zip"] || ""}`
          .replace(/^,\s*|,\s*$/g, "")
          .replace(/,\s*,/g, ",");
      updateMultipleFields({
        [name]: value,
        "Permanent city address": newCombined,
      });
    } else if (name === "Permanent address state NonUS") {
      // Handle state field changes and update combined field
      const newCombined =
        `${formData["Permanent address city"] || ""}, ${value}, ${formData["Permanent address zip"] || ""}`
          .replace(/^,\s*|,\s*$/g, "")
          .replace(/,\s*,/g, ",");
      updateMultipleFields({
        [name]: value,
        "Permanent city address": newCombined,
      });
    } else if (name === "Permanent address zip" || name === "Mailing address zip") {
      // Handle zip field saving specifically to ensure proper saving
      const updates: any = { [name]: value };

      // Also update the combined field if it exists
      if (name === "Permanent address zip") {
        updates["Permanent_address_city_state_postal"] =
          `${formData["Permanent address city"] || ""}, ${formData["Permanent address state NonUS"] || ""}, ${value}`
            .replace(/^,\s*|,\s*$/g, "")
            .replace(/,\s*,/g, ",");
      }
      if (name === "Mailing address zip") {
        updates["Mailing_address_city_state_postal"] =
          `${formData["Mailing address city"] || ""}, ${formData["Mailing address state NonUS"] || ""}, ${value}`
            .replace(/^,\s*|,\s*$/g, "")
            .replace(/,\s*,/g, ",");
      }

      updateMultipleFields(updates);
    } else if (name === "Mailing address city component") {
      // Handle mailing city field changes and update combined field
      const newCombined =
        `${value}, ${formData["Mailing address state NonUS"] || ""}, ${formData["Mailing address zip"] || ""}`
          .replace(/^,\s*|,\s*$/g, "")
          .replace(/,\s*,/g, ",");
      updateMultipleFields({
        [name]: value,
        "Mailing address city": newCombined,
      });
    } else if (name === "Mailing address state NonUS") {
      // Handle mailing state field changes and update combined field
      const newCombined =
        `${formData["Mailing address city"] || ""}, ${value}, ${formData["Mailing address zip"] || ""}`
          .replace(/^,\s*|,\s*$/g, "")
          .replace(/,\s*,/g, ",");
      updateMultipleFields({
        [name]: value,
        "Mailing address city": newCombined,
      });
    } else if (name === "Taxpayer TIN") {
      // Handle TIN formatting based on selected type
      const currentTaxpayerType = formData["Taxpayer type"] as string;
      let formattedValue = value;

      if (currentTaxpayerType === "EIN") {
        formattedValue = formatEIN(value);
      } else if (
        currentTaxpayerType === "QIEIN" ||
        currentTaxpayerType === "WPEIN" ||
        currentTaxpayerType === "WTEIN"
      ) {
        formattedValue = formatEIN(value);
      }

      updateField(name, formattedValue);
    } else if (name === "Taxpayer type") {
      // When taxpayer type changes, clear the TIN input so user can enter new value
      updateMultipleFields({
        [name]: value,
        "U.S. taxpayer identification number": "", // Clear TIN input
        "U.S. taxpayer identification number (if required)": "", // Clear alternate field name if it exists
      });

      // Clear validation message
      setTinValidationMessage("");
    } else if (name === "U.S. taxpayer identification number") {
      // Handle TIN input with validation (no max length restriction)
      const currentType = (formData["Taxpayer type"] as string) || "";
      const tinValue = value.toString().trim();

      // Update through model
      updateField(name, value);

      if (tinValue) {
        // Validate TIN format
        const validation = validateTINFormat(tinValue, currentType);

        // Set validation message
        setTinValidationMessage(validation.message);

        // If valid and detected type exists, and NO type is selected, auto-select it
        if (
          validation.isValid &&
          validation.detectedType &&
          (!currentType || currentType === "" || currentType === "---Select---")
        ) {
          let formType = validation.detectedType;
          if (formType === "SSN" || formType === "ITIN") {
            formType = "SSN/ITIN";
          }

          // Auto-update type
          updateField("Taxpayer type", formType);
        }
      } else {
        setTinValidationMessage(null);
      }
    } else {
      const newValue = type === "checkbox" ? checked : value;

      if (type === "checkbox") {
        return handleCheckboxChange(name, checked);
      }

      // Auto-detect taxpayer type based on TIN format
      if (name === "U.S. taxpayer identification number" && newValue) {
        const tinValue = newValue.toString().trim();

        // EIN format: XX-XXXXXXX (2 digits, dash, 7 digits)
        if (/^\d{2}-\d{7}$/.test(tinValue)) {
          updateField("Taxpayer type", "EIN");
        }
        // QIEIN format: XX-XXXXXXX-QI (2 digits, dash, 7 digits, dash, QI)
        else if (/^\d{2}-\d{7}-QI$/.test(tinValue)) {
          updateField("Taxpayer type", "QIEIN");
        }
        // WPEIN format: XX-XXXXXXX-WP (2 digits, dash, 7 digits, dash, WP)
        else if (/^\d{2}-\d{7}-WP$/.test(tinValue)) {
          updateField("Taxpayer type", "WPEIN");
        }
        // WTEIN format: XX-XXXXXXX-WT (2 digits, dash, 7 digits, dash, WT)
        else if (/^\d{2}-\d{7}-WT$/.test(tinValue)) {
          updateField("Taxpayer type", "WTEIN");
        }
      }

      // Update the model through the hook
      updateField(name, newValue);

      // Auto-check GIIN checkbox when GIIN field has a value and validate format
      if (name === "GIIN" && newValue && newValue.toString().trim()) {
        // 🛡️ CRITICAL PROTECTION: Only auto-check if form is not already mapped

        updateField("GIINCheckBox", true);

        // Validate GIIN format
        const giinValue = newValue.toString().trim();
        const giinPattern = /^[A-Z0-9]{6}\.[A-Z0-9]{5}\.[A-Z0-9]{2}\.[A-Z0-9]{3}$/i;

        if (giinPattern.test(giinValue)) {
          setGiinValidationMessage("✓ Valid GIIN format");
        } else {
          setGiinValidationMessage(
            "⚠️ Invalid GIIN format. Expected: XXXXXX.XXXXX.XX.XXX (e.g., G018AA.99999.SL.777)"
          );
        }
      } else if (name === "GIIN") {
        // Clear validation message when GIIN field is empty
        setGiinValidationMessage(null);
      }

      // Auto-check Foreign TIN checkbox when W8-BENE-Foreign_tax_identifying_number has a value
      if (
        name === "W8-BENE-Foreign_tax_identifying_number" &&
        newValue &&
        newValue.toString().trim()
      ) {
        // 🛡️ CRITICAL PROTECTION: Only auto-check if form is not already mapped
        if (!model?.isMapDone) {
          updateField("Foreign TIN", true);
        } else {
          console.log("🛡️ BLOCKED Foreign TIN auto-check - form already mapped");
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear any previous validation errors
    setShowValidationErrors(false);

    if (!validate()) {
      setShowValidationErrors(true);

      alert("Please fill in all required fields before submitting.");
      return;
    }

    try {
      // Use the model directly as the source of truth for current form values
      const currentFormValues: any = { ...model };

      // Add treaty claims data
      const treatyClaimsString = JSON.stringify(treatyClaims);
      currentFormValues.treatyClaims = treatyClaimsString;

      // Add individual treaty fields
      treatyClaims.forEach((claim, idx) => {
        currentFormValues[`W8-BENE-15-Treaty_article_${idx}`] = claim.article || "";
        currentFormValues[`W8-BENE-15-Treaty_paragraph_${idx}`] = claim.paragraph || "";
        currentFormValues[`W8-BENE-15-Treaty_subparagraph_${idx}`] = claim.subparagraph || "";
        currentFormValues[`W8-BENE-15-Treaty_percentage_${idx}`] = claim.percentage || "";
        currentFormValues[`W8-BENE-15-Treaty_typeOfIncome_${idx}`] = claim.typeOfIncome || "";
      });

      // Set mapping done flag to prevent re-mapping
      currentFormValues.isMapDone = true;

      // Remove specific fields before saving
      currentFormValues["Permanent city address"] = "";
      currentFormValues["Mailing address city"] = "";
      currentFormValues["Entity Type"] = "";
      currentFormValues["Chapter4Status "] = "";
      delete currentFormValues["W8-BENE-15_income_type"];
      delete currentFormValues["W8-BENE-15_withholding_rate_percentage"];
      delete currentFormValues["W8-BENE-15_treaty_provision_and_paragraph"];

      // Create a new model with the current form values
      const saveModel = new W8BENEFormModel(currentFormValues);

      // Save directly using the service with our collected current values
      const success = await W8BENEService.saveToApi(saveModel, row, selectedIndex);

      if (success) {
        // Clear validation errors on successful save
        setShowValidationErrors(false);

        if (onSaveSuccess) {
          onSaveSuccess(row);
        }
        alert("Form saved successfully");
      }
    } catch (error) {
      console.error("Failed to save W-8BEN-E form:", error);
      alert("Failed to save form data");
    }
  };

  return (
    <div className="scroll-container" style={headerStyle(false)}>
      {/* <h1 className="text-3xl font-bold text-center mb-8">Certificate of Status of Beneficial Owner for United States Tax Withholding and Reporting (Entities) (Form W-8BEN-E)</h1> */}
      <div
        style={{ width: "100%", display: "flex", flexDirection: "column", padding: 0, margin: 0 }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100% !important",
            minWidth: "100%",
            maxWidth: "100%",
            flex: "1 1 100%",
            display: "flex !important",
            flexDirection: "column",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", width: "100%", flexDirection: "column" }}>
            {/* Validation Summary - Only show after user tries to submit */}
            {showValidationErrors && Object.keys(errors).length > 0 && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                  padding: "12px",
                  margin: "16px 0",
                  color: "#b91c1c",
                }}
              >
                <h4 style={{ margin: "0 0 8px 0", color: "#991b1b" }}>
                  Please fix the following errors:
                </h4>
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {Object.entries(errors).map(([field, error], index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      <strong>{field.replace("W8-BENE-", "").replace(/_/g, " ")}:</strong> {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Part I: Identification of Beneficial Owner */}
            <Section title="Part I: Identification of Beneficial Owner">
              <div className="space-y-4">
                <FormInput
                  label={createRequiredLabel("Unique Form Identifier")}
                  id={"UniqueIdentifier"}
                  name={"UniqueIdentifier"}
                  value={(formData["UniqueIdentifier"] as string) || ""}
                  onChange={handleChange}
                />
                <FormInput
                  label={"Name of the organization:"}
                  id={"Name of organization"}
                  name={"Name of organization"}
                  value={formData["Name of organization"] || ""}
                  onChange={handleChange}
                />
                <CountryDropdown
                  label="Country of Organization:"
                  name="Country of incorporation or organization"
                  id="Country of incorporation or organization"
                  value={
                    typeof formData["Country of incorporation or organization"] === "string"
                      ? formData["Country of incorporation or organization"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Country of incorporation or organization",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
                <FormInput
                  label={"Other:"}
                  id={"Other"}
                  name={"Other"}
                  value={formData["Other"] || ""}
                  onChange={handleChange}
                />
                <FormInput
                  label={"Name of disregarded entity receiving the payment (if applicable):"}
                  id={"W8-BENE-Name_of_disregarded_entity"}
                  name={"W8-BENE-Name_of_disregarded_entity"}
                  value={formData["W8-BENE-Name_of_disregarded_entity"] || ""}
                  onChange={handleChange}
                />
                <FormsubTitle value={"Chapter 3 Status (entity type) (Must check one box only):"} />

                {/* Chapter 3 Status checkboxes - each with specific field name for proper handling */}
                <FormCheckBox
                  label={"Central Bank of Issue"}
                  name={"W8-BENE-4_central_bank"}
                  checked={!!formData["W8-BENE-4_central_bank"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Complex Trust"}
                  name={"W8-BENE-4_complex_trust"}
                  checked={!!formData["W8-BENE-4_complex_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Corporation"}
                  name={"W8-BENE-4_corporation"}
                  checked={!!formData["W8-BENE-4_corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Disregarded Entity"}
                  name={"W8-BENE-4_disregarded_entity"}
                  checked={!!formData["W8-BENE-4_disregarded_entity"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Estate"}
                  name={"W8-BENE-4_estate"}
                  checked={!!formData["W8-BENE-4_estate"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Government - Controlled Entity"}
                  name={"W8-BENE-4_foreign_government_controlled_entity"}
                  checked={!!formData["W8-BENE-4_foreign_government_controlled_entity"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Foreign Government - Integral Part"}
                  name={"W8-BENE-4_foreign_government_integral_part"}
                  checked={!!formData["W8-BENE-4_foreign_government_integral_part"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Grantor Trust"}
                  name={"W8-BENE-4_grantor_trust"}
                  checked={!!formData["W8-BENE-4_grantor_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"International Organization"}
                  name={"W8-BENE-4_international_organization"}
                  checked={!!formData["W8-BENE-4_international_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Partnership"}
                  name={"W8-BENE-4_partnership"}
                  checked={!!formData["W8-BENE-4_partnership"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Private Foundation"}
                  name={"W8-BENE-4_private_foundation"}
                  checked={!!formData["W8-BENE-4_private_foundation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Simple Trust"}
                  name={"W8-BENE-4_simple_trust"}
                  checked={!!formData["W8-BENE-4_simple_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Tax-Exempt Organization"}
                  name={"W8-BENE-4_tax_exempt_organization"}
                  checked={!!formData["W8-BENE-4_tax_exempt_organization"]}
                  onChange={handleChange}
                />
                <FormsubTitle
                  value={
                    "If you entered disregarded entity, partnership, simple trust, or grantor trust above, is the entity a hybrid making a treaty claim?"
                  }
                />
                <select
                  id="W8-BENE-4_hybrid_treaty_claim"
                  name="W8-BENE-4_hybrid_treaty_claim"
                  value={(formData["W8-BENE-4_hybrid_treaty_claim"] as string) || ""}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W8-BENE-4_hybrid_treaty_claim",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <FormsubTitle value={"Chapter 4 Status (FATCA Status):"} />

                {/* Chapter 4 Status checkboxes - each with specific field name for proper handling */}
                <FormCheckBox
                  label={"501(c) organization"}
                  name={"W8-BENE-5_fatca_501c_org_"}
                  checked={!!formData["W8-BENE-5_fatca_501c_org_"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Account that is not a financial account"}
                  name={"W8-BENE-5_account_not_financial"}
                  checked={!!formData["W8-BENE-5_account_not_financial"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Active NFFE"}
                  name={"W8-BENE-5_active_nffe"}
                  checked={!!formData["W8-BENE-5_active_nffe"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Certain investment entities that do not maintain financial accounts"}
                  name={"W8-BENE-5_investment_entities"}
                  checked={!!formData["W8-BENE-5_investment_entities"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Certified deemed-compliant FFI with only low-value accounts"}
                  name={"W8-BENE-5_certified_low_value"}
                  checked={!!formData["W8-BENE-5_certified_low_value"]}
                  onChange={handleChange}
                />
                {/* <FormCheckBox label={"Certified deemed-compliant investment advisors and investment managers"} name={"W8-BENE-5_certified_sponsored"} checked={!!formData["W8-BENE-5_certified_sponsored"]} onChange={handleChange} /> */}
                <FormCheckBox
                  label={"Certified deemed-compliant limited life debt investment entity"}
                  name={"W8-BENE-5_certified_limited_life"}
                  checked={!!formData["W8-BENE-5_certified_limited_life"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Certified deemed-compliant non-registering local bank"}
                  name={"W8-BENE-5_certified_local_bank"}
                  checked={!!formData["W8-BENE-5_certified_local_bank"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Certified deemed-compliant sponsored, closely held investment vehicle"}
                  name={"W8-BENE-5_certified_sponsored"}
                  checked={!!formData["W8-BENE-5_certified_sponsored"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Direct reporting NFFE"}
                  name={"W8-BENE-5_direct_reporting"}
                  checked={!!formData["W8-BENE-5_direct_reporting"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Entity wholly owned by exempt beneficial owners"}
                  name={"W8-BENE-5_entity_exempt_owners"}
                  checked={!!formData["W8-BENE-5_entity_exempt_owners"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Excepted inter-affiliate FFI"}
                  name={"W8-BENE-5_excepted_affiliate"}
                  checked={!!formData["W8-BENE-5_excepted_affiliate"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Excepted nonfinancial entity in liquidation or bankruptcy"}
                  name={"W8-BENE-5_excepted_liquidation"}
                  checked={!!formData["W8-BENE-5_excepted_liquidation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Excepted non-financial group entity"}
                  name={"W8-BENE-5_excepted_group"}
                  checked={!!formData["W8-BENE-5_excepted_group"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Excepted non-financial start-up company"}
                  name={"W8-BENE-5_excepted_startup"}
                  checked={!!formData["W8-BENE-5_excepted_startup"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Excepted territory NFFE"}
                  name={"W8-BENE-5_excepted_territory"}
                  checked={!!formData["W8-BENE-5_excepted_territory"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Exempt retirement plans"}
                  name={"W8-BENE-5_exempt_retirement"}
                  checked={!!formData["W8-BENE-5_exempt_retirement"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={
                    "Foreign government, government of a U.S. possession, or foreign central bank of issue"
                  }
                  name={"W8-BENE-5_foreign_government"}
                  checked={!!formData["W8-BENE-5_foreign_government"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"International organization"}
                  name={"W8-BENE-5_international_organization"}
                  checked={!!formData["W8-BENE-5_international_organization"]}
                  onChange={handleChange}
                />
                {/* <FormCheckBox label={"Nonfinancial group entity"} name={"W8-BENE-5_excepted_group"} checked={!!formData["W8-BENE-5_excepted_group"]} onChange={handleChange} /> */}
                <FormCheckBox
                  label={"Non-participating FFI"}
                  name={"W8-BENE-5_nonparticipating_ffi"}
                  checked={!!formData["W8-BENE-5_nonparticipating_ffi"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Nonprofit organization"}
                  name={"W8-BENE-5_nonprofit_org"}
                  checked={!!formData["W8-BENE-5_nonprofit_org"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Nonreporting IGA FFI"}
                  name={"W8-BENE-5_nonreporting_iga"}
                  checked={!!formData["W8-BENE-5_nonreporting_iga"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Owner documented FFI"}
                  name={"W8-BENE-5_owner_documented"}
                  checked={!!formData["W8-BENE-5_owner_documented"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Participating FFI"}
                  name={"W8-BENE-5_participating_ffi"}
                  checked={!!formData["W8-BENE-5_participating_ffi"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Passive NFFE"}
                  name={"W8-BENE-5_passive_nffe"}
                  checked={!!formData["W8-BENE-5_passive_nffe"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Publicly traded NFFE or NFFE affiliate of a publicly traded corporation"}
                  name={"W8-BENE-5_publicly_traded"}
                  checked={!!formData["W8-BENE-5_publicly_traded"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Registered deemed-compliant FFI"}
                  name={"W8-BENE-5_registered_deemed"}
                  checked={!!formData["W8-BENE-5_registered_deemed"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Reporting Model 1 FFI"}
                  name={"W8-BENE-5_reporting_model1"}
                  checked={!!formData["W8-BENE-5_reporting_model1"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Reporting Model 2 FFI"}
                  name={"W8-BENE-5_reporting_model2"}
                  checked={!!formData["W8-BENE-5_reporting_model2"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Restricted distributor"}
                  name={"W8-BENE-5_restricted_distributor"}
                  checked={!!formData["W8-BENE-5_restricted_distributor"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Sponsored direct reporting NFFE"}
                  name={"W8-BENE-5_sponsored_direct"}
                  checked={!!formData["W8-BENE-5_sponsored_direct"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Sponsored FFI"}
                  name={"W8-BENE-5_sponsored_ffi"}
                  checked={!!formData["W8-BENE-5_sponsored_ffi"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label={"Territory financial institution"}
                  name={"W8-BENE-5_territory_financial"}
                  checked={!!formData["W8-BENE-5_territory_financial"]}
                  onChange={handleChange}
                />
              </div>
            </Section>

            <Section title="Permanent address">
              {/* Main standardized address fields */}
              {/* Detailed address fields */}
              <FormInput
                label={"Line 1:"}
                id={"Permanent residence address"}
                name={"Permanent residence address"}
                value={(formData["Permanent residence address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Permanent address line 2"}
                name={"Permanent address line 2"}
                value={(formData["Permanent address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Permanent address line 3"}
                name={"Permanent address line 3"}
                value={(formData["Permanent address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Permanent address city"}
                name={"Permanent address city"}
                value={(formData["Permanent address city"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state USCA"}
                    name={"Permanent address state USCA"}
                    value={(formData["Permanent address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non-U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Permanent address state NonUS"}
                    name={"Permanent address state NonUS"}
                    value={(formData["Permanent address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Permanent address zip"}
                name={"Permanent address zip"}
                value={(formData["Permanent address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Permanent address country"
                id="Permanent address country"
                value={
                  typeof formData["Permanent address country"] === "string"
                    ? formData["Permanent address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Permanent address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Permanent address other"}
                name={"Permanent address other"}
                value={(formData["Permanent address other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is care-of-address: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="yes"
                      checked={formData["Permanent address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is care of"
                      value="no"
                      checked={formData["Permanent address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is P.O. Box: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="yes"
                      checked={formData["Permanent address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is po box"
                      value="no"
                      checked={formData["Permanent address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>
                    Is rural route: <span style={{ color: "red" }}>*</span>
                  </label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="yes"
                      checked={formData["Permanent address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Permanent address is rural route"
                      value="no"
                      checked={formData["Permanent address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </Section>

            <Section title="Mailing address">
              <FormInput
                label={"Line 1:"}
                id={"Mailing address"}
                name={"Mailing address"}
                value={(formData["Mailing address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Mailing address line 2"}
                name={"Mailing address line 2"}
                value={(formData["Mailing address line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 3:"}
                id={"Mailing address line 3"}
                name={"Mailing address line 3"}
                value={(formData["Mailing address line 3"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City / Town:"}
                id={"Mailing city address"}
                name={"Mailing city address"}
                value={(formData["Mailing city address"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>U.S.& Canada:</span> State / Province:
                    <span
                      style={{ color: "#fbbf24", marginLeft: 4, fontSize: 16 }}
                      title="For U.S. and Canada addresses only"
                    >
                      &#9432;
                    </span>
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state USCA"}
                    name={"Mailing address state USCA"}
                    value={(formData["Mailing address state USCA"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>
                    <span>Non-U.S.:</span> State / Province as it appears on form:
                  </label>
                  <FormInput
                    label={""}
                    id={"Mailing address state NonUS"}
                    name={"Mailing address state NonUS"}
                    value={(formData["Mailing address state NonUS"] as string) || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <FormInput
                label={"Zip / Postal Code:"}
                id={"Mailing address zip"}
                name={"Mailing address zip"}
                value={(formData["Mailing address zip"] as string) || ""}
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="Mailing address country"
                id="Mailing address country"
                value={
                  typeof formData["Mailing address country"] === "string"
                    ? formData["Mailing address country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "Mailing address country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
              <FormInput
                label={"Other:"}
                id={"Mailing address other"}
                name={"Mailing address other"}
                value={(formData["Mailing address other"] as string) || ""}
                onChange={handleChange}
              />
              {/* Yes/No radios */}
              <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <label>Is care-of-address:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="yes"
                      checked={formData["Mailing address is care of"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is care of"
                      value="no"
                      checked={formData["Mailing address is care of"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is P.O. Box:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="yes"
                      checked={formData["Mailing address is po box"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is po box"
                      value="no"
                      checked={formData["Mailing address is po box"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Is rural route:</label>
                  <br />
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="yes"
                      checked={formData["Mailing address is rural route"] === "yes"}
                      onChange={handleChange}
                    />{" "}
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="Mailing address is rural route"
                      value="no"
                      checked={formData["Mailing address is rural route"] === "no"}
                      onChange={handleChange}
                    />{" "}
                    No
                  </label>
                </div>
              </div>
            </Section>

            <Section title="Taxpayer identification">
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="Taxpayer type" style={{ display: "block", marginBottom: 4 }}>
                  Type:
                </label>
                <select
                  id="Taxpayer type"
                  name="Taxpayer type"
                  value={
                    typeof formData["Taxpayer type"] === "string" ? formData["Taxpayer type"] : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "Taxpayer type",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: 4,
                    border: "1px solid #ccc",
                  }}
                >
                  <option value="">---Select---</option>
                  <option value="EIN">EIN</option>
                  <option value="QIEIN">QIEIN</option>
                  <option value="WPEIN">WPEIN</option>
                  <option value="WTEIN">WTEIN</option>
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <FormInput
                  label="TIN:"
                  id="U.S. taxpayer identification number"
                  name="U.S. taxpayer identification number"
                  value={(formData["U.S. taxpayer identification number"] as string) || ""}
                  onChange={handleChange}
                  placeholder={
                    formData["Taxpayer type"] === "EIN"
                      ? "XX-XXXXXXX"
                      : formData["Taxpayer type"] === "QIEIN"
                        ? "XX-XXXXXXX-QI"
                        : formData["Taxpayer type"] === "WPEIN"
                          ? "XX-XXXXXXX-WP"
                          : formData["Taxpayer type"] === "WTEIN"
                            ? "XX-XXXXXXX-WT"
                            : "Enter TIN"
                  }
                />
                {tinValidationMessage && (
                  <div
                    style={{
                      marginTop: 4,
                      padding: "6px 8px",
                      backgroundColor: tinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                      border: `1px solid ${tinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                      borderRadius: 4,
                      fontSize: "12px",
                      color: tinValidationMessage.includes("✓") ? "#155724" : "#856404",
                    }}
                  >
                    {tinValidationMessage}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>GIIN:</label>
                <input
                  type="checkbox"
                  id="GIINCheckBox"
                  name="GIINCheckBox"
                  checked={!!formData["GIINCheckBox"]}
                  onChange={handleChange}
                  style={{ marginRight: 8 }}
                />
              </div>
              <FormInput
                label="GIIN Entered:"
                id="GIIN"
                name="GIIN"
                value={(formData["GIIN"] as string) || ""}
                onChange={handleChange}
              />

              {/* GIIN Validation Message */}
              {giinValidationMessage && (
                <div
                  style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: "8px 12px",
                    borderRadius: 4,
                    fontSize: "14px",
                    backgroundColor: giinValidationMessage.includes("✓") ? "#d4edda" : "#fff3cd",
                    border: `1px solid ${giinValidationMessage.includes("✓") ? "#28a745" : "#ffc107"}`,
                    display: "flex",
                    alignItems: "center",
                    color: giinValidationMessage.includes("✓") ? "#155724" : "#856404",
                  }}
                >
                  {giinValidationMessage}
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>GIIN "Applied For":</label>
                <input
                  type="checkbox"
                  id="GIIN Applied For"
                  name="GIIN Applied For"
                  checked={!!formData["GIIN Applied For"]}
                  onChange={handleChange}
                  style={{ marginRight: 8 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 4 }}>Foreign TIN:</label>
                <input
                  type="checkbox"
                  id="Foreign TIN"
                  name="Foreign TIN"
                  checked={!!formData["Foreign TIN"]}
                  onChange={handleChange}
                  style={{ marginRight: 8 }}
                />
              </div>
              <FormInput
                label="Foreign taxpayer id:"
                id="W8-BENE-Foreign_tax_identifying_number"
                name="W8-BENE-Foreign_tax_identifying_number"
                value={(formData["W8-BENE-Foreign_tax_identifying_number"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 16 }}>
                <label>
                  <input
                    type="checkbox"
                    name="W8-BENE-FTIN_not_legally_required"
                    checked={!!formData["W8-BENE-FTIN_not_legally_required"]}
                    onChange={handleChange}
                    style={{ marginRight: 8 }}
                  />
                  Check if FTIN not legally required :
                </label>
              </div>
              <FormInput
                label="Reference number(s):"
                id="W8-BENE-10_reference_number"
                name="W8-BENE-10_reference_number"
                value={(formData["W8-BENE-10_reference_number"] as string) || ""}
                onChange={handleChange}
              />
            </Section>

            <Section title="Part II Disregarded Entity or Branch Receiving Payment. (Complete only if disregarded entity with a GIIN or branch of an FFI in a country other than the FFI's country of residence.)">
              <FormsubTitle
                value={
                  "11 Chapter 4 status (FATCA Status) of disregarded entity or branch receiving payment."
                }
              />
              <FormCheckBox
                label={"Branch treated as nonparticipating FFI"}
                name={"W8-BENE-11_branch_treated_as_nonparticipating_FFI"}
                checked={!!formData["W8-BENE-11_branch_treated_as_nonparticipating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Participating FFI"}
                name={"W8-BENE-11_participating_FFI"}
                checked={!!formData["W8-BENE-11_participating_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 1 FFI"}
                name={"W8-BENE-11_reporting_Model1_FFI"}
                checked={!!formData["W8-BENE-11_reporting_Model1_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"Reporting Model 2 FFI"}
                name={"W8-BENE-11_reporting_Model2_FFI"}
                checked={!!formData["W8-BENE-11_reporting_Model2_FFI"]}
                onChange={handleChange}
              />
              <FormCheckBox
                label={"U.S. Branch"}
                name={"W8-BENE-11_us_branch"}
                checked={!!formData["W8-BENE-11_us_branch"]}
                onChange={handleChange}
              />
              <FormsubTitle value={"12  Disregarded entity or branch address"} />
              <FormInput
                label={"Line 1:"}
                id={"W8-BENE-12_disregarded_entity_address"}
                name={"W8-BENE-12_disregarded_entity_address"}
                value={(formData["W8-BENE-12_disregarded_entity_address"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Line 2:"}
                id={"Address of disregarded entity line 2"}
                name={"Address of disregarded entity line 2"}
                value={(formData["Address of disregarded entity line 2"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"City, State, and Postal Code:"}
                id={"W8-BENE-12_disregarded_entity_city_state_postal"}
                name={"W8-BENE-12_disregarded_entity_city_state_postal"}
                value={
                  (formData["W8-BENE-12_disregarded_entity_city_state_postal"] as string) || ""
                }
                onChange={handleChange}
              />
              <CountryDropdown
                label="Country:"
                name="W8-BENE-12_disregarded_entity_country"
                id="W8-BENE-12_disregarded_entity_country"
                value={
                  typeof formData["W8-BENE-12_disregarded_entity_country"] === "string"
                    ? formData["W8-BENE-12_disregarded_entity_country"]
                    : ""
                }
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "W8-BENE-12_disregarded_entity_country",
                      value: e.target.value,
                      type: "select-one",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />

              <FormInput
                label={"13 GIIN (if any):"}
                id={"W8-BENE-13_giin"}
                name={"W8-BENE-13_giin"}
                value={(formData["W8-BENE-13_giin"] as string) || ""}
                onChange={handleChange}
              />
            </Section>

            <Section title="Part III Claim of Tax Treaty Benefits">
              <FormsubTitle value="(if applicable). (For Chapter 3 purposes only)" />
              <div style={{ marginBottom: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    name="W8-BENE-14A_beneficial_owner_resident"
                    checked={!!formData["W8-BENE-14A_beneficial_owner_resident"]}
                    onChange={handleChange}
                  />{" "}
                  14 a
                </label>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>Resident of:</label>
                <CountryDropdown
                  label=""
                  name="W8-BENE-14A_country_name"
                  id="W8-BENE-14A_country_name"
                  value={
                    typeof formData["W8-BENE-14A_country_name"] === "string"
                      ? formData["W8-BENE-14A_country_name"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W8-BENE-14A_country_name",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    name="W8-BENE-14B_beneficial_owner_derives_item"
                    checked={!!formData["W8-BENE-14B_beneficial_owner_derives_item"]}
                    onChange={handleChange}
                  />{" "}
                  14 b
                </label>
              </div>
              <div style={{ marginLeft: 24, marginBottom: 8 }}>
                <FormCheckBox
                  label="Government"
                  name="W8-BENE-14B_government"
                  checked={!!formData["W8-BENE-14B_government"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Tax exempt Pension trust or pension fund"
                  name="W8-BENE-14B_tax_exempt_pension_trust"
                  checked={!!formData["W8-BENE-14B_tax_exempt_pension_trust"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Other tax-exempt organization"
                  name="W8-BENE-14B_other_tax_exempt_organization"
                  checked={!!formData["W8-BENE-14B_other_tax_exempt_organization"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Publicly traded corporation"
                  name="W8-BENE-14B_publicly_traded_corporation"
                  checked={!!formData["W8-BENE-14B_publicly_traded_corporation"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Subsidiary of a publicly traded corporation"
                  name="W8-BENE-14B_subsidiary_of_publicly_traded"
                  checked={!!formData["W8-BENE-14B_subsidiary_of_publicly_traded"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Company that meets the ownership and base erosion test"
                  name="W8-BENE-14B_company_meets_ownership_and_base_erosion_test"
                  checked={!!formData["W8-BENE-14B_company_meets_ownership_and_base_erosion_test"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Company that meets the derivative benefits test"
                  name="W8-BENE-14B_company_meets_derivative"
                  checked={!!formData["W8-BENE-14B_company_meets_derivative"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Company with an item of income that meets active trade or business test"
                  name="W8-BENE-14B_company_meets_active_trade_or_business"
                  checked={!!formData["W8-BENE-14B_company_meets_active_trade_or_business"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Favorable discretionary determination by the U.S. competent authority received"
                  name="W8-BENE-14B_favorable_discretionary_determination"
                  checked={!!formData["W8-BENE-14B_favorable_discretionary_determination"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="No LOB article in treaty"
                  name="W8-BENE-14B_no_lob"
                  checked={!!formData["W8-BENE-14B_no_lob"]}
                  onChange={handleChange}
                />
                <FormCheckBox
                  label="Other (Specify Article and Paragraph)"
                  name="W8-BENE-14B_other"
                  checked={!!formData["W8-BENE-14B_other"]}
                  onChange={handleChange}
                />
                <FormInput
                  label="Other (Specify Article and Paragraph):"
                  id="W8-BENE-14B_other_text"
                  name="W8-BENE-14B_other_text"
                  value={(formData["W8-BENE-14B_other_text"] as string) || ""}
                  onChange={handleChange}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  <input
                    type="checkbox"
                    name="W8-BENE-14C_claiming_treaty_benefits"
                    checked={!!formData["W8-BENE-14C_claiming_treaty_benefits"]}
                    onChange={handleChange}
                  />{" "}
                  14 c
                </label>
              </div>
              <FormsubTitle value="15 Special rates and conditions" />
              {/* Repeatable claim fields */}
              {treatyClaims.map((claim: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid #eee",
                    padding: 16,
                    marginBottom: 16,
                    borderRadius: 4,
                  }}
                >
                  <FormInput
                    label="Claimed Article:"
                    id={`Treaty claimed article-${idx}`}
                    name={`Treaty claimed article-${idx}`}
                    value={claim.article || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Paragraph:"
                    id={`Treaty claimed paragraph-${idx}`}
                    name={`Treaty claimed paragraph-${idx}`}
                    value={claim.paragraph || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Subparagraph Information (if any):"
                    id={`Treaty subparagraph info-${idx}`}
                    name={`Treaty subparagraph info-${idx}`}
                    value={claim.subparagraph || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed Percentage:"
                    id={`Treaty claimed percentage-${idx}`}
                    name={`Treaty claimed percentage-${idx}`}
                    value={claim.percentage || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />
                  <FormInput
                    label="Claimed type of income:"
                    id={`Treaty claimed type of income-${idx}`}
                    name={`Treaty claimed type of income-${idx}`}
                    value={claim.typeOfIncome || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleTreatyClaimChange(idx, e)
                    }
                  />

                  {/* Action buttons row */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 16,
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleAddTreatyClaim}
                      style={{
                        background: "#0094e8",
                        color: "white",
                        fontWeight: 600,
                        border: "none",
                        borderRadius: 2,
                        padding: "10px 24px",
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                    >
                      ADD ANOTHER
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveTreatyClaim(idx)}
                      style={{
                        color: "red",
                        background: "none",
                        border: "none",
                        fontSize: 20,
                        cursor: "pointer",
                        padding: "8px",
                      }}
                      title="Remove"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}

              {/* Add button for when no claims exist */}
              {/* {treatyClaims.length === 0 && (
                <button
                  type="button"
                  onClick={handleAddTreatyClaim}
                  style={{
                    margin: "8px 0 16px 0",
                    background: "#0094e8",
                    color: "white",
                    fontWeight: 600,
                    border: "none",
                    borderRadius: 2,
                    padding: "10px 24px",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                >
                  ADD ANOTHER
                </button>
              )} */}
              <div style={{ marginTop: 16 }}>
                <label>Claim Explanation:</label>
                <textarea
                  name="W8-BENE-15_additional_conditions_explaination"
                  value={
                    (formData["W8-BENE-15_additional_conditions_explaination"] as string) || ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e as any)}
                  style={{
                    width: "100%",
                    minHeight: 80,
                    borderRadius: 4,
                    border: "1px solid #ccc",
                    padding: 8,
                  }}
                />
              </div>
            </Section>

            <Section title="Part IV Sponsored FFI">
              <FormInput
                label={"16 Name of the sponsoring entity:"}
                id={"W8-BENE-16_name_of_sponsoring_entity"}
                name={"W8-BENE-16_name_of_sponsoring_entity"}
                value={(formData["W8-BENE-16_name_of_sponsoring_entity"] as string) || ""}
                onChange={handleChange}
              />
              <FormsubTitle value={"17 Check whichever box applies"} />
              <div style={{ marginBottom: 8 }}>
                <label>
                  17 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-17_investment_entity"
                    checked={!!formData["W8-BENE-17_investment_entity"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  17 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-17_controlled_foreign_corporation"
                    checked={!!formData["W8-BENE-17_controlled_foreign_corporation"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part V Certified Deemed-Compliant Non-registering Local Bank">
              <div style={{ marginBottom: 8 }}>
                <label>
                  18{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-18_licensed_solely"
                    checked={!!formData["W8-BENE-18_licensed_solely"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part VI Certified Deemed-Compliant FFI with Only Low-Value Accounts">
              <div style={{ marginBottom: 8 }}>
                <label>
                  19{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-19_investing_reinvesting_or_trading"
                    checked={!!formData["W8-BENE-19_investing_reinvesting_or_trading"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part VII Certified Deemed-Compliant Sponsored, Closely Held Investment Vehicle">
              <FormInput
                label={"20 Name of the sponsoring entity:"}
                id={"W8-BENE-20_sponsoring_entity_name"}
                name={"W8-BENE-20_sponsoring_entity_name"}
                value={(formData["W8-BENE-20_sponsoring_entity_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label>
                  21{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-21_qi_wp_wt"
                    checked={!!formData["W8-BENE-21_qi_wp_wt"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part VIII Certified Deemed-Compliant FFI Limited Life Debt Investment Entity">
              <div style={{ marginBottom: 8 }}>
                <label>
                  22{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-22_january_17_2013"
                    checked={!!formData["W8-BENE-22_january_17_2013"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part IX Certain Investment Entities that Do Not Maintain Financial Accounts">
              <div style={{ marginBottom: 8 }}>
                <label>
                  23{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-23_section_1_1471"
                    checked={!!formData["W8-BENE-23_section_1_1471"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part X Owner-Documented FFI">
              <div style={{ marginBottom: 8, color: "#444", fontSize: 14 }}>
                <b>Note:</b> This status only applies if the U.S. financial institution or
                participating FFI to which this form is given has agreed that it will treat the FFI
                as an owner-documented FFI (see instructions for eligibility requirements). In
                addition, the FFI must make the certifications below.
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  24 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-24A_owner_documented_FFI"
                    checked={!!formData["W8-BENE-24A_owner_documented_FFI"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 24b or 24c, whichever applies
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  24 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-24B_FFI_owner_reporting"
                    checked={!!formData["W8-BENE-24B_FFI_owner_reporting"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  24 c{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-24C_auditor_letter"
                    checked={!!formData["W8-BENE-24C_auditor_letter"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Check box 24d if applicable</div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  24 d{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-24D_contingent_beneficiaries"
                    checked={!!formData["W8-BENE-24D_contingent_beneficiaries"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XI Restricted Distributor">
              <div style={{ marginBottom: 8 }}>
                <label>
                  25 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-25A_restricted_distributors"
                    checked={!!formData["W8-BENE-25A_restricted_distributors"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 25b or 25c, whichever applies
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  25 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-25B_restricted_distributor_bound_agreement"
                    checked={!!formData["W8-BENE-25B_restricted_distributor_bound_agreement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  25 c{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-25C_restricted_distributor_current_agreement"
                    checked={!!formData["W8-BENE-25C_restricted_distributor_current_agreement"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XII Nonreporting IGA FFI">
              <div style={{ marginBottom: 8 }}>
                <label>
                  26{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-26_nonreporting_iga_country"
                    checked={!!formData["W8-BENE-26_nonreporting_iga_country"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Country:</label>
                <CountryDropdown
                  label=""
                  name="W8-BENE-26_country_name"
                  id="W8-BENE-26_country_name"
                  value={
                    typeof formData["W8-BENE-26_country_name"] === "string"
                      ? formData["W8-BENE-26_country_name"]
                      : ""
                  }
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "W8-BENE-26_country_name",
                        value: e.target.value,
                        type: "select-one",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                />
              </div>
              <FormInput
                label={"Other:"}
                id={"W8-BENE-26_country_other"}
                name={"W8-BENE-26_country_other"}
                value={(formData["W8-BENE-26_country_other"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label>
                  Model 1 IGA:{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-26_model1_iga"
                    checked={!!formData["W8-BENE-26_model1_iga"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  Model 2 IGA:{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-26_model2_iga"
                    checked={!!formData["W8-BENE-26_model2_iga"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <FormInput
                label={"Treated as:"}
                id={"W8-BENE-26_treated_as"}
                name={"W8-BENE-26_treated_as"}
                value={(formData["W8-BENE-26_treated_as"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label={"Name of Trustee or Sponsor:"}
                id={"W8-BENE-26_trustee_sponsor_name"}
                name={"W8-BENE-26_trustee_sponsor_name"}
                value={(formData["W8-BENE-26_trustee_sponsor_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label>Trustee Is: </label>
                <label style={{ marginLeft: 8, marginRight: 8 }}>
                  <input
                    type="checkbox"
                    name="W8-BENE-26_trustee_us"
                    checked={!!formData["W8-BENE-26_trustee_us"]}
                    onChange={handleChange}
                  />{" "}
                  U.S.
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="W8-BENE-26_trustee_foreign"
                    checked={!!formData["W8-BENE-26_trustee_foreign"]}
                    onChange={handleChange}
                  />{" "}
                  Foreign
                </label>
              </div>
            </Section>
            <Section title="Part XIII Foreign Government, Government of a U.S. Possession, or Foreign Central Bank of Issue">
              <div style={{ marginBottom: 8 }}>
                <label>
                  27{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-27_foreign_government_checkbox"
                    checked={!!formData["W8-BENE-27_foreign_government_checkbox"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XIV International Organization">
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 28a or 28b, whichever applies
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  28 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-28A_international_org_7701_checkbox"
                    checked={!!formData["W8-BENE-28A_international_org_7701_checkbox"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  28 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-28B_international_org_intergovernmental"
                    checked={!!formData["W8-BENE-28B_international_org_intergovernmental"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XV Exempt Retirement Plans">
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 29a, b, c, d, e or f, whichever applies
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29A_retirement_plan_treaty"
                    checked={!!formData["W8-BENE-29A_retirement_plan_treaty"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29B_retirement_plan_organized"
                    checked={!!formData["W8-BENE-29B_retirement_plan_organized"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 c{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29C_retirement_plan_fewer_50"
                    checked={!!formData["W8-BENE-29C_retirement_plan_fewer_50"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 d{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29D_retirement_plan_401a"
                    checked={!!formData["W8-BENE-29D_retirement_plan_401a"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 e{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29E_retirement_plan_earn_income"
                    checked={!!formData["W8-BENE-29E_retirement_plan_earn_income"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  29 f{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-29F_retirement_plan_government_sponsored"
                    checked={!!formData["W8-BENE-29F_retirement_plan_government_sponsored"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XVI Entity Wholly Owned by Exempt Beneficial Owners">
              <div style={{ marginBottom: 8 }}>
                <label>
                  30{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-30_entity_wholly_owned"
                    checked={!!formData["W8-BENE-30_entity_wholly_owned"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XVII Territory Financial Institution">
              <div style={{ marginBottom: 8 }}>
                <label>
                  31{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-31_territory_financial_institution"
                    checked={!!formData["W8-BENE-31_territory_financial_institution"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XVIII Excepted Nonfinancial Group Entity">
              <div style={{ marginBottom: 8 }}>
                <label>
                  32{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-32_excepted_nonfinancial_group"
                    checked={!!formData["W8-BENE-32_excepted_nonfinancial_group"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XIX Excepted Nonfinancial Start-Up Company">
              <div style={{ marginBottom: 8 }}>
                <label>
                  33{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-33_excepted_startup"
                    checked={!!formData["W8-BENE-33_excepted_startup"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Formed on Date:</div>
              <div style={{ marginBottom: 16, maxWidth: 300 }}>
                <FormDate
                  label=""
                  name="W8-BENE-33_excepted_startup_formation_date"
                  value={formData["W8-BENE-33_excepted_startup_formation_date"] || ""}
                  onChange={handleChange}
                />
                <span style={{ marginLeft: 8, color: "#888" }}>(U.S. Date format)</span>
              </div>
            </Section>
            <Section title="Part XX Excepted Nonfinancial Entity in Liquidation or Bankruptcy">
              <div style={{ marginBottom: 8 }}>
                <label>
                  34{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-34_excepted_liquidation"
                    checked={!!formData["W8-BENE-34_excepted_liquidation"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              {/* <FormInput label={"Bankruptcy"} id={"W8-BENE-34_excepted_liquidation_filing_date"} name={"W8-BENE-34_excepted_liquidation_filing_date"} value={formData["W8-BENE-34_excepted_liquidation_filing_date"] as string || ''} onChange={handleChange} /> */}

              <div style={{ marginBottom: 8, fontWeight: 500 }}>Liquidation Date:</div>
              <div style={{ marginBottom: 16, maxWidth: 300 }}>
                {/* <FormDate label="" name="BENE-34-liquidation_date" value={formData["BENE-34-liquidation_date"] || ''} onChange={handleChange} /> */}
                <FormDate
                  label=""
                  name="W8-BENE-34_excepted_liquidation_filing_date"
                  value={formData["W8-BENE-34_excepted_liquidation_filing_date"] || ""}
                  onChange={handleChange}
                />
                <span style={{ marginLeft: 8, color: "#888" }}>(U.S. Date format)</span>
              </div>
            </Section>
            <Section title="Part XXI 501(c) Organization">
              <div style={{ marginBottom: 8 }}>
                <label>
                  35{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE_35_section_501c"
                    checked={!!formData["W8-BENE_35_section_501c"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              {/* <FormInput label={"Organization"} id={"W8-BENE-35_section_501c_determination_date"} name={"W8-BENE-35_section_501c_determination_date"} value={formData["W8-BENE-35_section_501c_determination_date"] as string || ''} onChange={handleChange} /> */}

              <div style={{ marginBottom: 8, fontWeight: 500 }}>Letter Date:</div>
              <div style={{ marginBottom: 16, maxWidth: 300 }}>
                {/* <FormDate label="" name="BENE-35-letter_date" value={formData["BENE-35-letter_date"] || ''} onChange={handleChange} /> */}
                <FormDate
                  label=""
                  name="W8-BENE-35_section_501c_determination_date"
                  value={formData["W8-BENE-35_section_501c_determination_date"] || ""}
                  onChange={handleChange}
                />
                <span style={{ marginLeft: 8, color: "#888" }}>(U.S. Date format)</span>
              </div>
            </Section>
            <Section title="Part XXII Non-profit Organization">
              <div style={{ marginBottom: 8 }}>
                <label>
                  36{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-36_nonprofit_organization"
                    checked={!!formData["W8-BENE-36_nonprofit_organization"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXIII Publicly Traded NFFE or NFFE Affiliate of a Publicly Traded Corporation">
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 37a or 37b, whichever applies:
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  37 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-37A_publicly_traded_nffe"
                    checked={!!formData["W8-BENE-37A_publicly_traded_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <FormInput
                label="Securities Exchange:"
                id="W8-BENE-37A_securities_exchange_name"
                name="W8-BENE-37A_securities_exchange_name"
                value={(formData["W8-BENE-37A_securities_exchange_name"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label>
                  37 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-37B_nffe_affiliate"
                    checked={!!formData["W8-BENE-37B_nffe_affiliate"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <FormInput
                label="Entity Name:"
                id="W8-BENE-37B_entity_name"
                name="W8-BENE-37B_entity_name"
                value={(formData["W8-BENE-37B_entity_name"] as string) || ""}
                onChange={handleChange}
              />
              <FormInput
                label="Securities Market:"
                id="W8-BENE-37B_name_of_securities"
                name="W8-BENE-37B_name_of_securities"
                value={(formData["W8-BENE-37B_name_of_securities"] as string) || ""}
                onChange={handleChange}
              />
            </Section>
            <Section title="Part XXIV Excepted Territory NFFE">
              <div style={{ marginBottom: 8 }}>
                <label>
                  38{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-38_excepted_territory_nffe"
                    checked={!!formData["W8-BENE-38_excepted_territory_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXV Active NFFE">
              <div style={{ marginBottom: 8 }}>
                <label>
                  39{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-39_active_nffe"
                    checked={!!formData["W8-BENE-39_active_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXVI Passive NFFE">
              <div style={{ marginBottom: 8 }}>
                <label>
                  40 a{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-40A_traded_nffe"
                    checked={!!formData["W8-BENE-40A_traded_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                Check box 40b or 40c, whichever applies:
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  40 b{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-40B_passive_nffe_no_substantial_owners"
                    checked={!!formData["W8-BENE-40B_passive_nffe_no_substantial_owners"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>
                  40 c{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-40C_passive_nffe_substantial_owners_provided"
                    checked={!!formData["W8-BENE-40C_passive_nffe_substantial_owners_provided"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXVII Excepted Inter-Affiliate FFI">
              <div style={{ marginBottom: 8 }}>
                <label>
                  41{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-41_affiliated_group"
                    checked={!!formData["W8-BENE-41_affiliated_group"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXVIII Sponsored Direct Reporting NFFE">
              <FormInput
                label="42 Name of the sponsoring entity:"
                id="W8-BENE-42_sponsoring_entity"
                name="W8-BENE-42_sponsoring_entity"
                value={(formData["W8-BENE-42_sponsoring_entity"] as string) || ""}
                onChange={handleChange}
              />
              <div style={{ marginBottom: 8 }}>
                <label>
                  43{" "}
                  <input
                    type="checkbox"
                    name="W8-BENE-43_nffe"
                    checked={!!formData["W8-BENE-43_nffe"]}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </Section>
            <Section title="Part XXIX Substantial U.S. Owners of Passive NFFE">
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16, fontWeight: 500 }}>
                  Complete this section if you checked box 40c in Part XXVI above. Provide the name,
                  address, and TIN of each substantial U.S. owner.
                </div>

                {/* Substantial Owners Table */}
                <div style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      fontWeight: "bold",
                      padding: "8px",
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #ddd",
                    }}
                  >
                    <div>Name</div>
                    <div>Address</div>
                    <div>TIN</div>
                  </div>

                  {/* Row 1 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_1_name"
                      name="W8-BENE-substantial_owner_1_name"
                      value={(formData["W8-BENE-substantial_owner_1_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_1_address"
                      name="W8-BENE-substantial_owner_1_address"
                      value={(formData["W8-BENE-substantial_owner_1_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_1_tin"
                      name="W8-BENE-substantial_owner_1_tin"
                      value={(formData["W8-BENE-substantial_owner_1_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 2 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_2_name"
                      name="W8-BENE-substantial_owner_2_name"
                      value={(formData["W8-BENE-substantial_owner_2_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_2_address"
                      name="W8-BENE-substantial_owner_2_address"
                      value={(formData["W8-BENE-substantial_owner_2_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_2_tin"
                      name="W8-BENE-substantial_owner_2_tin"
                      value={(formData["W8-BENE-substantial_owner_2_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 3 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_3_name"
                      name="W8-BENE-substantial_owner_3_name"
                      value={(formData["W8-BENE-substantial_owner_3_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_3_address"
                      name="W8-BENE-substantial_owner_3_address"
                      value={(formData["W8-BENE-substantial_owner_3_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_3_tin"
                      name="W8-BENE-substantial_owner_3_tin"
                      value={(formData["W8-BENE-substantial_owner_3_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 4 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_4_name"
                      name="W8-BENE-substantial_owner_4_name"
                      value={(formData["W8-BENE-substantial_owner_4_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_4_address"
                      name="W8-BENE-substantial_owner_4_address"
                      value={(formData["W8-BENE-substantial_owner_4_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_4_tin"
                      name="W8-BENE-substantial_owner_4_tin"
                      value={(formData["W8-BENE-substantial_owner_4_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 5 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_5_name"
                      name="W8-BENE-substantial_owner_5_name"
                      value={(formData["W8-BENE-substantial_owner_5_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_5_address"
                      name="W8-BENE-substantial_owner_5_address"
                      value={(formData["W8-BENE-substantial_owner_5_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_5_tin"
                      name="W8-BENE-substantial_owner_5_tin"
                      value={(formData["W8-BENE-substantial_owner_5_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 6 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_6_name"
                      name="W8-BENE-substantial_owner_6_name"
                      value={(formData["W8-BENE-substantial_owner_6_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_6_address"
                      name="W8-BENE-substantial_owner_6_address"
                      value={(formData["W8-BENE-substantial_owner_6_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_6_tin"
                      name="W8-BENE-substantial_owner_6_tin"
                      value={(formData["W8-BENE-substantial_owner_6_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 7 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_7_name"
                      name="W8-BENE-substantial_owner_7_name"
                      value={(formData["W8-BENE-substantial_owner_7_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_7_address"
                      name="W8-BENE-substantial_owner_7_address"
                      value={(formData["W8-BENE-substantial_owner_7_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_7_tin"
                      name="W8-BENE-substantial_owner_7_tin"
                      value={(formData["W8-BENE-substantial_owner_7_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 8 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_8_name"
                      name="W8-BENE-substantial_owner_8_name"
                      value={(formData["W8-BENE-substantial_owner_8_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_8_address"
                      name="W8-BENE-substantial_owner_8_address"
                      value={(formData["W8-BENE-substantial_owner_8_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_8_tin"
                      name="W8-BENE-substantial_owner_8_tin"
                      value={(formData["W8-BENE-substantial_owner_8_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 9 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_9_name"
                      name="W8-BENE-substantial_owner_9_name"
                      value={(formData["W8-BENE-substantial_owner_9_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_9_address"
                      name="W8-BENE-substantial_owner_9_address"
                      value={(formData["W8-BENE-substantial_owner_9_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_9_tin"
                      name="W8-BENE-substantial_owner_9_tin"
                      value={(formData["W8-BENE-substantial_owner_9_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>

                  {/* Row 10 */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr 1fr",
                      gap: "10px",
                      marginBottom: 8,
                      padding: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_10_name"
                      name="W8-BENE-substantial_owner_10_name"
                      value={(formData["W8-BENE-substantial_owner_10_name"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_10_address"
                      name="W8-BENE-substantial_owner_10_address"
                      value={(formData["W8-BENE-substantial_owner_10_address"] as string) || ""}
                      onChange={handleChange}
                      placeholder="Complete Address"
                    />
                    <FormInput
                      label=""
                      id="W8-BENE-substantial_owner_10_tin"
                      name="W8-BENE-substantial_owner_10_tin"
                      value={(formData["W8-BENE-substantial_owner_10_tin"] as string) || ""}
                      onChange={handleChange}
                      placeholder="TIN"
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16, fontSize: "0.9em", color: "#666" }}>
                  <strong>Note:</strong> Complete only if you checked box 40c in Part XXVI. List all
                  substantial U.S. owners (generally, a U.S. person that owns 10% or more of the
                  stock or profits interest in the entity).
                </div>
              </div>
            </Section>

            <section style={sectionStyle}>
              <FormTitle value={"Part XXX Certification"} />
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    id="Signing_capacity"
                    checked={Boolean(formData.Signing_capacity)}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: "Signing_capacity",
                          type: "checkbox",
                          checked: e.target.checked,
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor="Signing_capacity">
                    certify that I have the capacity to sign for the entity identified on line 1 of
                    this form.
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Is signed: <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="yes"
                      checked={formData.isSigned === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isSigned"
                      value="no"
                      checked={formData.isSigned === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isSigned", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span className="form-label">
                    Has the form certification been modified:{" "}
                    <span style={{ color: "red" }}>*</span>
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="yes"
                      checked={formData.isModified === "yes"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "yes" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    Yes
                  </label>
                  <label style={{ marginRight: 16 }}>
                    <input
                      type="radio"
                      name="isModified"
                      value="no"
                      checked={formData.isModified === "no"}
                      onChange={(e) =>
                        handleChange({
                          target: { name: "isModified", value: "no" },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />{" "}
                    No
                  </label>
                </div>
                <FormInput
                  label={"Signed Name:"}
                  id={"Signer_printed_name"}
                  value={formData["Signer_printed_name"]}
                  name={"Signer_printed_name"}
                  onChange={handleChange}
                />
                {/* <FormInput label={"Signature:"} id={"Signature"} value={formData["Signature"]} name={"Signature"} onChange={handleChange} /> */}

                <div style={{ marginBottom: "1rem" }}>
                  <FormDate
                    label="Date:"
                    name="Signature_date"
                    value={formData["Signature_date"] || ""}
                    onChange={handleChange}
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 12,
                      marginTop: 12,
                    }}
                  >
                    <input
                      type="checkbox"
                      id="Signature_unreadable"
                      checked={formData.Signature_unreadable || false}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: "Signature_unreadable",
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      style={{ marginRight: 8 }}
                    />
                    <label htmlFor="Signature_unreadable">Check if signature is unreadable</label>
                  </div>
                </div>
              </div>
            </section>
            <div
              style={{
                display: "flex",
                marginTop: 20,
                marginBottom: 40,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                maxWidth: "100%",
                alignItems: "center",
                boxSizing: "border-box",
              }}
            >
              <button
                type="submit"
                style={{
                  padding: "6px 16px",
                  backgroundColor: InstaColors.primary,
                  color: "white",
                  fontWeight: 500,
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderRadius: "0.375rem",
                  boxShadow: "none",
                  cursor: "pointer",
                  border: "none",
                }}
              >
                Save Form
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Section component for consistent styling
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div style={sectionStyle}>
      <FormTitle value={title} />
      {children}
    </div>
  );
};

// Form field component for consistent styling
const FormField = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
};

// Checkbox field component
const CheckboxField = ({
  name,
  checked = false,
  onChange,
  label,
}: {
  name: string;
  checked?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <div>
        <input
          id={name}
          name={name}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </div>
      <div style={{ marginLeft: 8 }}>
        <label htmlFor={name} className="font-medium text-gray-700">
          {label}
        </label>
      </div>
    </div>
  );
};

export default W8BENEForm;
